import { Avatar, Box, Chip, Link, Stack, Tooltip, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import moment from "moment";

import { LaunchpadPurchase } from "../../api/transactionNftAPIs";
import { accountingFormat } from "../../utils/converText";
import {
  bscScanAddressUrl,
  bscScanTxUrl,
  shortenHash,
} from "../../utils/explorer";
import { REPORT_PALETTE as PALETTE } from "../../utils/palette.token";

export const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  completed: { bg: "rgba(3,194,149,0.15)", color: "#03C295" },
  released: { bg: "rgba(3,194,149,0.15)", color: "#03C295" },
  escrowed: { bg: "rgba(232,200,106,0.15)", color: "#E8C86A" },
  release_pending: { bg: "rgba(232,200,106,0.15)", color: "#E8C86A" },
  refund_pending: { bg: "rgba(232,200,106,0.15)", color: "#E8C86A" },
  pending: { bg: "rgba(110,200,240,0.15)", color: "#6EC8F0" },
  failed: { bg: "rgba(255,107,107,0.15)", color: "#FF6B6B" },
  refunded: { bg: "rgba(255,107,107,0.15)", color: "#FF6B6B" },
};

export const LAUNCHPAD_PURCHASE_STATUSES = [
  "completed",
  "escrowed",
  "pending",
  "failed",
  "released",
  "refunded",
] as const;

const emptyCell = (
  <Typography sx={{ color: PALETTE.muted, fontSize: "0.85rem" }}>—</Typography>
);

const moneyCell = (
  value: number,
  symbol: string,
  emphasis: boolean
) => (
  <Stack direction="row" alignItems="baseline" spacing={0.5}>
    <Typography
      sx={{
        fontVariantNumeric: "tabular-nums",
        fontWeight: emphasis ? 700 : 500,
        fontSize: emphasis ? "0.9rem" : "0.85rem",
        color: emphasis ? PALETTE.textPrimary : PALETTE.textSecondary,
      }}
    >
      {accountingFormat(value)}
    </Typography>
    <Typography sx={{ color: PALETTE.muted, fontSize: "0.7rem" }}>
      {symbol}
    </Typography>
  </Stack>
);

export const columnsLaunchpadPurchase = (): GridColDef[] => [
  {
    field: "createdAt",
    headerName: "Date",
    minWidth: 108,
    flex: 0.9,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({ row }: GridRenderCellParams<any, LaunchpadPurchase>) =>
      row.createdAt ? (
        <Stack>
          <Typography sx={{ fontSize: "0.85rem", color: PALETTE.textPrimary }}>
            {moment(row.createdAt).format("DD/MM/YYYY")}
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", color: PALETTE.muted }}>
            {moment(row.createdAt).format("HH:mm")}
          </Typography>
        </Stack>
      ) : (
        emptyCell
      ),
  },
  {
    field: "collectionName",
    headerName: "Launchpad",
    minWidth: 184,
    flex: 1.5,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({ row }: GridRenderCellParams<any, LaunchpadPurchase>) => (
      <Stack direction="row" alignItems="center" spacing={1.2} sx={{ minWidth: 0 }}>
        <Avatar
          src={row.collectionImage || undefined}
          variant="rounded"
          sx={{ width: 32, height: 32, bgcolor: PALETTE.accentDim, fontSize: "0.8rem" }}
        >
          {(row.collectionName || "?").charAt(0)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Tooltip title={row.collectionName || row.collectionId || ""}>
            <Typography
              noWrap
              sx={{ fontSize: "0.85rem", color: PALETTE.textPrimary, fontWeight: 500 }}
            >
              {row.collectionName || row.collectionId || "—"}
            </Typography>
          </Tooltip>
          <Typography noWrap sx={{ fontSize: "0.7rem", color: PALETTE.muted }}>
            {row.quantity} NFT
          </Typography>
        </Box>
      </Stack>
    ),
  },
  {
    field: "buyerEmail",
    headerName: "Buyer",
    minWidth: 182,
    flex: 1.4,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({ row }: GridRenderCellParams<any, LaunchpadPurchase>) => (
      <Stack sx={{ minWidth: 0 }}>
        <Tooltip title={row.buyerEmail || ""}>
          <Typography noWrap sx={{ fontSize: "0.82rem", color: PALETTE.textPrimary }}>
            {row.buyerEmail || "—"}
          </Typography>
        </Tooltip>
        {row.buyerWalletAddress && (
          <Link
            href={bscScanAddressUrl(row.buyerWalletAddress)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.7rem",
              color: PALETTE.muted,
              textDecoration: "none",
              "&:hover": { color: PALETTE.accent, textDecoration: "underline" },
            }}
          >
            {shortenHash(row.buyerWalletAddress)}
          </Link>
        )}
      </Stack>
    ),
  },
  {
    field: "pricePerNft",
    headerName: "Price / NFT",
    minWidth: 108,
    flex: 0.9,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({ row }: GridRenderCellParams<any, LaunchpadPurchase>) =>
      moneyCell(row.pricePerNft, row.currencySymbol, false),
  },
  {
    field: "totalPrice",
    headerName: "Total",
    minWidth: 118,
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({ row }: GridRenderCellParams<any, LaunchpadPurchase>) =>
      moneyCell(row.totalPrice, row.currencySymbol, true),
  },
  {
    field: "sellerAmount",
    headerName: "Seller (90%)",
    minWidth: 118,
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({ row }: GridRenderCellParams<any, LaunchpadPurchase>) =>
      moneyCell(row.sellerAmount, row.currencySymbol, false),
  },
  {
    field: "platformFee",
    headerName: "Fee (10%)",
    minWidth: 108,
    flex: 0.9,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({ row }: GridRenderCellParams<any, LaunchpadPurchase>) =>
      moneyCell(row.platformFee, row.currencySymbol, false),
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 104,
    flex: 0.8,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({ row }: GridRenderCellParams<any, LaunchpadPurchase>) => {
      // For vault-backed purchases `status` is frozen at "escrowed", so the
      // settlement state is the one worth showing.
      const effective = row.settlementStatus || row.status;
      if (!effective) return emptyCell;
      const tone = STATUS_COLORS[effective] || {
        bg: "rgba(255,255,255,0.08)",
        color: PALETTE.muted,
      };
      return (
        <Tooltip
          title={
            row.settlementStatus
              ? `Vault settlement state (transaction record still reads "${row.status}")`
              : ""
          }
        >
          <Chip
            label={effective.replace(/_/g, " ")}
            size="small"
            sx={{
              bgcolor: tone.bg,
              color: tone.color,
              fontWeight: 600,
              fontSize: "0.68rem",
              height: 22,
              textTransform: "capitalize",
            }}
          />
        </Tooltip>
      );
    },
  },
  {
    field: "txHash",
    headerName: "Transaction",
    minWidth: 132,
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({ row }: GridRenderCellParams<any, LaunchpadPurchase>) => {
      if (!row.txHash) return emptyCell;
      return (
        <Tooltip title="View on BscScan">
          <Link
            href={bscScanTxUrl(row.txHash)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontFamily: "monospace",
              fontSize: "0.78rem",
              color: PALETTE.accent,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {shortenHash(row.txHash)}
            <OpenInNewIcon sx={{ fontSize: 13 }} />
          </Link>
        </Tooltip>
      );
    },
  },
];
